import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Tag } from 'lucide-react';

function CalendarComponent({ fechasDisponibles, onDateSelect, selectedDate, destino }) {
  // Calcular mes inicial basado en primera fecha disponible
  const getInitialMonth = () => {
    if (fechasDisponibles && fechasDisponibles.length > 0) {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      const fechasOrdenadas = [...fechasDisponibles]
        .map(f => new Date(f.fecha))
        .filter(d => !isNaN(d.getTime()) && d >= hoy)
        .sort((a, b) => a - b);
      
      if (fechasOrdenadas.length > 0) {
        console.log('📅 Primera fecha disponible:', fechasOrdenadas[0]);
        return fechasOrdenadas[0];
      }
    }
    return new Date();
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth);

  // Actualizar mes cuando cambian las fechas disponibles (ej: cambio de destino)
  useEffect(() => {
    if (fechasDisponibles && fechasDisponibles.length > 0) {
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      const fechasOrdenadas = [...fechasDisponibles]
        .map(f => new Date(f.fecha))
        .filter(d => !isNaN(d.getTime()) && d >= hoy)
        .sort((a, b) => a - b);
      
      if (fechasOrdenadas.length > 0) {
        setCurrentMonth(fechasOrdenadas[0]);
      }
    }
  }, [fechasDisponibles]);
  
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  // Generar días del calendario
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Días vacíos del mes anterior
    for (let i = 0; i < firstDayOfWeek; i++) {
      const prevDate = new Date(year, month, -firstDayOfWeek + i + 1);
      days.push({
        date: prevDate,
        isCurrentMonth: false,
        day: prevDate.getDate()
      });
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date: date,
        isCurrentMonth: true,
        day: day
      });
    }
    
    // Días del siguiente mes para completar la grilla
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({
        date: nextDate,
        isCurrentMonth: false,
        day: nextDate.getDate()
      });
    }
    
    return days;
  }, [currentMonth]);

  // Función para obtener información de fecha específica
  const getDateInfo = (date) => {
    const calendarDateString = date.getFullYear() + '-' + 
      String(date.getMonth() + 1).padStart(2, '0') + '-' + 
      String(date.getDate()).padStart(2, '0');
    
    const fechaInfo = fechasDisponibles.find(f => {
      const fechaPaquete = new Date(f.fecha);
      const paqueteDateString = fechaPaquete.getFullYear() + '-' + 
        String(fechaPaquete.getMonth() + 1).padStart(2, '0') + '-' + 
        String(fechaPaquete.getDate()).padStart(2, '0');
      
      return paqueteDateString === calendarDateString;
    });
    
    return fechaInfo || null;
  };

  // ✅ FUNCIÓN CORREGIDA: Detectar si un paquete tiene descuento PRINCIPAL (sin validar otros descuentos)
  const tieneDescuentos = (fechaInfo) => {
    if (!fechaInfo) {
      return false;
    }
    
    // Revisar en fechaInfo.paquete si existe, sino en fechaInfo directamente
    const paquete = fechaInfo.paquete || fechaInfo;
    
    // ✅ SOLO verificar descuento_principal_monto (SIN importar si tiene family plan, 2do o 3er pasajero)
    if (paquete.descuento_principal_monto && parseFloat(paquete.descuento_principal_monto) > 0) {
      return true; // ← SIN validar fechas de vigencia
    }
    
    return false;
  };

  // Función para obtener clases CSS del día
  const getDayClasses = (dayInfo, conDescuento) => {
    const { date, isCurrentMonth } = dayInfo;
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    const isPast = date < today;
    const fechaInfo = getDateInfo(date);
    const isSelected = selectedDate && date.toDateString() === new Date(selectedDate).toDateString();
    
    // ✅ Celdas más grandes: w-16 h-14 (antes era w-12 h-10)
    let classes = `w-16 h-14 text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center justify-center relative ${
      conDescuento ? '' : 'rounded-lg'
    } `;
    
    if (!isCurrentMonth) {
      classes += 'text-gray-300 cursor-not-allowed ';
    } else if (isPast) {
      classes += 'text-gray-400 cursor-not-allowed ';
    } else if (isSelected) {
      classes += 'bg-[#2AA8B0] text-white shadow-lg ring-2 ring-[#2AA8B0] ring-offset-2 ';
    } else if (fechaInfo) {
      if (fechaInfo.destacado) {
        classes += 'bg-yellow-400 text-white shadow-md hover:bg-yellow-500 cursor-pointer ring-2 ring-yellow-300 ';
      } else if (conDescuento) {
        // ✅ Color especial para paquetes con descuento
        classes += 'bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-lg hover:from-orange-600 hover:to-red-600 cursor-pointer ';
      } else {
        classes += 'bg-blue-500 text-white shadow-md hover:bg-blue-600 cursor-pointer ring-1 ring-blue-400 ';
      }
    } else if (isToday) {
      classes += 'bg-gray-100 text-gray-900 ring-2 ring-gray-300 cursor-pointer hover:bg-gray-200 ';
    } else {
      classes += 'text-gray-700 hover:bg-gray-100 cursor-pointer ';
    }
    
    return classes;
  };

  // Navegar meses
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (dayInfo) => {
    const { date, isCurrentMonth } = dayInfo;
    const today = new Date();
    const isPast = date < today;
    const fechaInfo = getDateInfo(date);
    
    if (isCurrentMonth && !isPast && fechaInfo) {
      onDateSelect(date, fechaInfo);
    }
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header del calendario */}
      <div className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] px-6 py-4">
        <div className="flex items-center justify-between text-white">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-1">
              <select
                value={currentMonth.getMonth()}
                onChange={(e) => setCurrentMonth(new Date(currentMonth.getFullYear(), parseInt(e.target.value), 1))}
                className="bg-white/20 text-white border border-white/30 rounded-lg px-3 py-1 text-sm font-semibold focus:outline-none focus:bg-white/30"
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index} className="text-gray-900">
                    {month}
                  </option>
                ))}
              </select>
              
              <select
                value={currentMonth.getFullYear()}
                onChange={(e) => setCurrentMonth(new Date(parseInt(e.target.value), currentMonth.getMonth(), 1))}
                className="bg-white/20 text-white border border-white/30 rounded-lg px-3 py-1 text-sm font-semibold focus:outline-none focus:bg-white/30"
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return (
                    <option key={year} value={year} className="text-gray-900">
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <p className="text-sm text-blue-100">
              Fechas disponibles para {destino}
            </p>
          </div>
          
          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {dayNames.map(day => (
          <div key={day} className="p-2 text-center text-xs font-semibold text-gray-600">
            {day}
          </div>
        ))}
      </div>

      {/* Grilla del calendario */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((dayInfo, index) => {
            const fechaInfo = getDateInfo(dayInfo.date);
            const conDescuento = tieneDescuentos(fechaInfo);
            
            return (
              <div key={index} className="relative flex items-center justify-center">
                {/* ✅ Fondo de estrella si tiene descuento */}
                {fechaInfo && dayInfo.isCurrentMonth && conDescuento && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Star 
                      size={48} 
                      className="fill-orange-500 text-orange-500 animate-pulse drop-shadow-lg"
                      style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
                    />
                  </div>
                )}
                
                <button
                  onClick={() => handleDateClick(dayInfo)}
                  className={getDayClasses(dayInfo, conDescuento)}
                  disabled={!dayInfo.isCurrentMonth || dayInfo.date < new Date() || !fechaInfo}
                  title={fechaInfo ? 
                    `${fechaInfo.titulo} - ${fechaInfo.descuento > 0 ? 
                      Math.round(fechaInfo.precio * (1 - fechaInfo.descuento / 100)) : 
                      fechaInfo.precio}${fechaInfo.descuento > 0 ? 
                        ` (${fechaInfo.descuento}% desc)` : ''}${fechaInfo.destacado ? ' (Destacado)' : ''}${conDescuento ? ' 🎁 Con descuentos' : ''}` : 
                    ''}
                  style={conDescuento && fechaInfo && dayInfo.isCurrentMonth ? {
                    background: 'transparent',
                    zIndex: 10
                  } : {}}
                >
                  <span className={conDescuento && fechaInfo && dayInfo.isCurrentMonth ? 
                    'relative z-10 font-bold text-white drop-shadow-md' : ''
                  }>
                    {dayInfo.day}
                  </span>
                </button>
                
                {/* Indicador de precio para fechas con paquetes */}
                {fechaInfo && dayInfo.isCurrentMonth && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 z-20">
                    <div className={`text-xs px-1 rounded shadow-sm border flex items-center gap-0.5 ${
                      conDescuento 
                        ? 'bg-orange-100 text-orange-800 border-orange-400 font-bold' 
                        : 'bg-white text-gray-700 border-gray-200'
                    }`}>
                      {conDescuento && (
                        <Tag size={8} className="text-orange-600" />
                      )}
                      ${(() => {
                          // ✅ Obtener el paquete completo
                          const paquete = fechaInfo.paquete || fechaInfo;
                          
                          // 🔍 DEBUG: Ver qué datos tiene el paquete
                          console.log('📅 DEBUG Calendario - Paquete:', {
                            id: paquete.id,
                            titulo: paquete.titulo,
                            tarifa: paquete.tarifa,
                            impuestos: paquete.impuestos,
                            descuento_principal_monto: paquete.descuento_principal_monto,
                            paquete_completo: paquete
                          });
                          
                          // Precio base (tarifa) - YA VIENE CON DESCUENTO APLICADO
                          let precioBase = parseFloat(paquete.tarifa || fechaInfo.precio || 0);
                          
                          // ✅ Aplicar descuento principal si existe y está activo
                          let descuentoMonto = 0;
                          if (paquete.descuento_principal_monto && parseFloat(paquete.descuento_principal_monto) > 0) {
                            const ahora = new Date();
                            const desde = paquete.descuento_principal_desde ? new Date(paquete.descuento_principal_desde) : null;
                            const hasta = paquete.descuento_principal_hasta ? new Date(paquete.descuento_principal_hasta) : null;
                            
                            const descuentoActivo = (!desde || ahora >= desde) && (!hasta || ahora <= hasta);
                            
                            if (descuentoActivo) {
                              if (paquete.descuento_principal_tipo === 'porcentaje') {
                                descuentoMonto = (precioBase * parseFloat(paquete.descuento_principal_monto)) / 100;
                              } else {
                                descuentoMonto = parseFloat(paquete.descuento_principal_monto);
                              }
                            }
                          }
                          
                          // Restar descuento del precio base
                          const precioConDescuento = precioBase - descuentoMonto;
                          
                          // Impuestos
                          const impuestos = parseFloat(paquete.impuestos || 0);
                          
                          console.log('💰 Cálculo final:', {
                            precioBase,
                            descuentoMonto,
                            precioConDescuento,
                            impuestos,
                            total: precioConDescuento + impuestos
                          });
                          
                          // Total = precio con descuento + impuestos
                          const total = precioConDescuento + impuestos;
                          
                          return Math.round(total).toLocaleString('es-AR');
                        })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer con leyenda */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex flex-wrap justify-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-gray-600">Fechas disponibles</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-400 rounded"></div>
            <span className="text-gray-600">Paquetes destacados</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#2AA8B0] rounded"></div>
            <span className="text-gray-600">Fecha seleccionada</span>
          </div>
          {/* ✅ NUEVA LEYENDA */}
          <div className="flex items-center gap-2">
            <Star size={12} className="fill-orange-500 text-orange-500" />
            <span className="text-gray-600">Con descuentos</span>
          </div>
        </div>
        
        {fechasDisponibles.length > 0 && (
          <div className="text-center mt-3">
            <button
              onClick={goToToday}
              className="text-[#2AA8B0] hover:text-[#1E3A5F] font-medium text-sm"
            >
              Ir a hoy
            </button>
          </div>
        )}
      </div>
      
      {/* Mensaje si no hay fechas */}
      {fechasDisponibles.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <div className="mx-auto w-8 h-8 border-2 border-gray-300 rounded mb-2"></div>
          <p>No hay fechas disponibles para este destino</p>
        </div>
      )}
    </div>
  );
}

export default CalendarComponent;